import { Breadcrumb, Col, Row, Pagination, Table, Statistic, Select, Button, Space } from "antd";
import { ColumnsType } from "antd/lib/table";
import React, { useEffect, useState, useRef } from "react";
import { Link } from "react-router-dom";
import { useLazyGetListPrepaymentQuery } from "../redux/ProfileApi";
import { ListResponseFile, PrepaySearch } from "../types";
import { ReactComponent as ListWait } from '../../../assets/images/list_wait.svg';
import { ReactComponent as ListSuccess } from '../../../assets/images/list_success.svg';
import { ReactComponent as ListError } from '../../../assets/images/list_error.svg';
// import { ReactComponent as ListAction } from '../../../assets/images/list_action.svg';
import { format } from "date-fns";
import PrepayFilter from "./components/PrepayFilter";
// import AuctionHistoryDetail from "../auction-history/components/AuctionHistoryDetail";
import { exportFile } from "../../common/ts/CommonFunctions";

const { Option } = Select;

interface PrepayTable {
    key: number,
    code: string,
    status: number,
    propertyCode: string,
    property: {
        name: string,
        id: number,
    },
    transactionDate: number,
    modifiedDate?: number,
    amount: number,
    receipt: string,
    bidderId: number,
    file: ListResponseFile[],
}

const statusIcon: Record<number, { icon: React.ReactNode, title: string }> = {
    1: { icon: <ListWait />, title: "Chờ đối soát" },
    2: { icon: <ListSuccess />, title: "Đã nộp tiền" },
    0: { icon: <ListError />, title: "Chưa nộp tiền" },
    3: { icon: <ListError />, title: "Thất bại" },
}

const PrepayList = () => {
    const [trigger, response] = useLazyGetListPrepaymentQuery();
    const [page, setPage] = useState(1);
    const [submitValue, setSubmitValue] = useState<PrepaySearch>();
    const [pageSize, setPageSize] = useState(10);
    const [prepayList, setPrepayList] = useState<PrepayTable[]>([]);
    // const [historyVisible, setHistoryVisible] = useState(false);

    const divRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        trigger({
            page: 0,
            size: 10
        })
    }, [trigger])

    useEffect(() => {
        let newList: PrepayTable[] = [];
        if (response.data?.content?.length) {
            newList = response.data.content.map(item => {
                return {
                    amount: item.amount,
                    code: item.tradingCode,
                    key: item.id,
                    modifiedDate: item.createdDate,
                    property: {
                        id: item.product.productId,
                        name: item.product.productName || "-"
                    },
                    propertyCode: item.product.productCode || "-",
                    statuteCode: item.statute?.code || "-",
                    receipt: "",
                    status: item.tradingStatus,
                    transactionDate: item.tradingDate,
                    bidderId: item.bidder.bidderId,
                    file: item.filesBienLai,
                }
            })
        }
        setPrepayList(newList);
        scrollToStart();
    }, [response])

    const triggerSearch = (currentSubmitValue?: PrepaySearch) => {
        trigger({
            page: page - 1,
            size: pageSize,
            ...(currentSubmitValue || submitValue),
        })
    }

    const prepayTableColumns: ColumnsType<PrepayTable> = [
        {
            title: 'STT',
            dataIndex: 'stt',
            key: 'stt',
            width: 70,
            align: 'center',
            render: (value, record, index) => {
                return (<span
                    style={{ border: '1px solid black', padding: '7px 13px', borderRadius: '20px', fontWeight: 'bold', whiteSpace: 'nowrap' }}
                    key={index}>
                    {(page - 1) * pageSize + index + 1}
                </span>)
            }
        },
        {
            title: 'Mã giao dịch',
            dataIndex: 'code',
            key: 'code',
            width: 95,
        },
        {
            title: 'Trạng thái giao dịch',
            dataIndex: 'status',
            key: 'status',
            render: (value: number) => {
                return (<div style={{ display: 'flex', alignItems: 'center' }}>
                    {statusIcon[value]?.icon || <></>}<span style={{ marginLeft: 5 }}>{statusIcon[value]?.title || ""}</span>
                </div>)
            }
        },
        {
            title: "Mã quy chế",
            dataIndex: "statuteCode",
            key: 'statuteCode',
            width: 100,
        },
        {
            title: "Mã tài sản",
            dataIndex: "propertyCode",
            key: 'propertyCode',
            width: 100,
        },
        {
            title: "Tài sản",
            dataIndex: "property",
            key: "property",
            render: (value, record) => {
                return value.name || "-"
            }
        },
        {
            title: "Thời gian giao dịch",
            dataIndex: "transactionDate",
            key: 'transactionDate',
            render: (value: number) => value ? format(value, "dd/MM/yyyy HH:mm:ss") : "-"
        },
        {
            title: "Thời gian gửi đối soát",
            dataIndex: "modifiedDate",
            key: 'modifiedDate',
            render: (value: number) => value ? format(value, "dd/MM/yyyy HH:mm:ss") : "-"
        },
        {
            title: "Số tiền giao dịch",
            dataIndex: "amount",
            key: 'amount',
            render: (value: number) => (
                <Statistic
                    value={value}
                    groupSeparator="."
                    decimalSeparator=","
                    precision={0}
                    valueStyle={{ fontFamily: 'Quicksand', fontSize: 14, fontWeight: 'bold' }} />
            )
        },
        {
            title: "Biên lai",
            dataIndex: "file",
            key: 'file',
            width: 200,
            ellipsis: true,
            render: (value: ListResponseFile[]) => {
                if (value?.[0]?.fileName && value?.[0]?.fileUrl) {
                    return (
                        <span className="anchor-text" onClick={() => downloadFile(value?.[0]?.fileUrl, value?.[0]?.fileName)}>{value?.[0]?.fileName || ""}</span>
                    )
                }

                return <></>;
            }
        },
    ]

    const scrollToStart = () => {
        if (divRef.current) {
            divRef.current.scrollIntoView({ behavior: 'smooth' })
        }
    }

    const onPageChange = (currentPage: number) => {
        setPage(currentPage);
        triggerSearch({
            ...submitValue,
            page: currentPage - 1,
        })
    }

    const onPageSizeChange = (currentSize: number) => {
        setPageSize(currentSize);
        setPage(1);
        triggerSearch({
            ...submitValue,
            page: 0,
            size: currentSize,
        })
    }

    // const toggleModal = () => {
    //     setHistoryVisible(!historyVisible);
    // }

    const downloadFile = (url: string, fileName: string) => {
        exportFile(url, fileName);
    }

    const exportExcel = () => {
        let url = process.env.REACT_APP_API_URL + "/product/auction-register/export-prepayment?page=0&properties=modifiedDate&size=10000&sortBy=DESC"
        let filename = `${format(new Date(), "dd-MM-yyyy")}_Theo dõi thông tin tiền đặt trước.xlsx`;

        if (submitValue && Object.keys(submitValue).length) {
            Object.entries(submitValue).forEach((item) => {
                if (item[1]) {
                    url += `&${item[0]}=${item[1]}`;
                }
            })
        }

        exportFile(url, filename);
    }

    return (
        <div
            ref={divRef}
            style={{
                // padding: '40px calc((100vw - 1440px) / 2) 40px',
                background: "#ececec",
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexDirection: 'column',
                width: '100%',
                paddingTop: 40,
                paddingBottom: 40,
            }}
        >
            <Space style={{ display: 'flex', alignItems: 'center', marginBottom: 20 }} className="responsive-padding-1440">
                <Breadcrumb separator="  " className="custom-breadcrumb">
                    <Breadcrumb.Item>
                        <Link to="/">
                            Trang chủ
                        </Link>
                    </Breadcrumb.Item>
                    <Breadcrumb.Item>
                        Theo dõi thông tin giao dịch tiền đặt trước
                    </Breadcrumb.Item>
                </Breadcrumb>

                <div className="responsive-title">Theo dõi thông tin giao dịch tiền đặt trước</div>
            </Space>
            <div className="responsive-padding-1440">
                <Row gutter={30}>
                    <Col span={6}>
                        <PrepayFilter setSubmitValue={setSubmitValue} triggerSearch={triggerSearch} setPage={setPage} />
                    </Col>
                    <Col span={18}>
                        <Row align="middle" style={{ whiteSpace: 'pre-wrap' }} justify="space-between">
                            <span>Tổng số <strong>{response.data?.totalElements || 0}</strong> bản ghi</span>
                            <Button
                                onClick={exportExcel}
                                className="button-light-yellow"
                                style={{ padding: '11px 22px', display: 'flex', alignItems: 'center' }}>Xuất dữ liệu</Button>
                        </Row>

                        <div className='custom-table-wrapper'>
                            <Table
                                loading={response.isLoading || response.isFetching}
                                columns={prepayTableColumns}
                                dataSource={prepayList}
                                pagination={false}
                                scroll={{ x: 1300 }}
                            />
                        </div>

                        <Row justify="end" align="middle" style={{ marginTop: 35 }}>
                            <Select className="left-arrow-select" defaultValue={10} value={pageSize} onChange={onPageSizeChange} dropdownStyle={{ borderRadius: 15 }}>
                                <Option value={10}>10</Option>
                                <Option value={20}>20</Option>
                                <Option value={50}>50</Option>
                                <Option value={100}>100</Option>
                            </Select>
                            <div style={{ marginRight: 20, marginLeft: 8, fontSize: 14 }}>Bản ghi</div>
                            <Pagination
                                defaultCurrent={1}
                                total={response.data?.totalElements || 0}
                                defaultPageSize={pageSize}
                                className="custom-pagination"
                                current={page}
                                pageSize={pageSize}
                                onChange={onPageChange}
                                showSizeChanger={false}
                            />
                        </Row>
                    </Col>
                </Row>
            </div>

            {/* {historyVisible ? <AuctionHistoryDetail
                visible={historyVisible}
                bidderId={bidderId || 0}
                toggleModal={toggleModal}
            /> : <></>} */}
        </div>
    )
}

export default PrepayList